<template>
	<div id="calc" class="container container-fluid qrs-typography" @click.stop.prevent="handleClick">

		<p>
			The Malnutrition Universal Screening Tool (MUST) is a screening tool provided by 
			<a href="https://www.bapen.org.uk/screening-and-must/must/introducing-must" target="_blank">BAPEN: the British Association for Parenteral and Enteral Nutrition</a>. 
			It is used to identify adults, who are malnourished, at risk of malnutrition (undernutrition), 
			or obese. It also includes management guidelines which can be used to develop a care plan.
		</p>

		<Tabs />
		<Warning />

		<InputWeight @inputPress="hideResult()" @inputChange="checkExceededWeight()" ref="currentWeight" :id="inputQuestions[0].id" :title="inputQuestions[0].title" :description="inputQuestions[0].description"></InputWeight>
		<InputWeightPrevious :errorWeight="exceededWeight" @inputPress="hideResult()" @inputChange="checkExceededWeight()" ref="monthsAgo"  :id="inputQuestions[1].id" :title="inputQuestions[1].title" :description="inputQuestions[1].description"></InputWeightPrevious>
		
		<div id="assess" class="questions__intro-text mb-2 mb--2">
			<div class="row">
				<div class="col-12 col-xs-12">
					<p>How to assess nutritional status if height, weight or BMI cannot be obtained – see <a href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf" target="_blank">BAPEN MUST explanatory booklet</a>.</p>
				</div>
			</div>
		</div>
		<hr class="mb-2 mb--2" />

		<Question ref="haveOedema" :q="questions[0]" @click="hideResult()" @keydown.enter="hideResult()"/>		
		
		<div :class="{'hidden' : answers.haveOedema.value != 1}">			
			<Question ref="isOedema" :q="additionalQuestions[0]" @click="hideResult()" @keydown.enter="hideResult()"/>
			<Question ref="present" :q="additionalQuestions[1]" @click="hideResult()" @keydown.enter="hideResult()"/>
		</div>		
		
		<Question ref="isAmputations" :q="questions[1]" @click="hideResult()" @keydown.enter="hideResult()"/>	
				
		<div :class="{'hidden' : checkAmpultations() }">
			<SelectParts ref="amputations" title="Adjustment for amputation" description="If the patient is an amputee, select missing limb segments" @change="toggleAmputationMessage()" :errorLower="lowerBodyAmputations" :errorUpper="upperBodyAmputations"/>
		</div>

		<div :class="{'hidden' : checkWeight() }">
			<Question ref="weightLoss" :q="questions[2]" @click="hideResult()" @keydown.enter="hideResult()"/>
		</div>

		<InputHeight @inputPress="hideResult()" ref="currentHeight" :id="inputQuestions[2].id" :title="inputQuestions[2].title" :description="inputQuestions[2].description"></InputHeight>

		<div id="estimate-bmi" class="questions__intro-text mb-2 mb--2">
			<div class="row">
				<div class="col-12 col-xs-12">
					<p><a href="https://www.bapen.org.uk/pdfs/must/must_explan.pdf" target="_blank">Estimating BMI if neither height nor weight cannot be measured</a> – from BAPEN MUST explanatory booklet.</p>
				</div>
			</div>
		</div>
		<hr class="mb-2 mb--2" />

		<InputText @inputPress="hideResult()" ref="dateCurrent" id="dateMeasured" title="Date current height measured"></InputText>
		
		<Question ref="acute" :q="questions[3]" @click="hideResult()" @keydown.enter="hideResult()"/>

		<!-- Calculator Error Section -->				
		<div id="error" v-if="displayErrorMessage" class="alert alert-info alert--info alert-informative mb-3 mb--3" role="alert">
			<h2>Missing input</h2>
			<p>Total MUST score and risk level cannot be calculated as one or more inputs are missing.</p>
		</div>


		<!-- Calculator Result Section -->
		<div class="calculator__result bg-secondary bg--secondary p--2 p-2 mb--2 mb-2">
			<!-- Calculator Copy Section -->
			<div id="calculator-copy" class="color--white text-white mb-1 mb--1" v-if="displayresult">
				<Responses :answers="answers" :result="result" :error="displayErrorMessage" :errorWeight="exceededWeightAdjusted" :errorLower="lowerBodyAmputations" :errorUpper="upperBodyAmputations" :errorBmi="InvalidBmi" :errorWeightLoss="InvalidWeightLoss"/>
				<Low v-if="result.score == 0 && !displayErrorMessage && !lowerBodyAmputations && !upperBodyAmputations" :bmi="result.bmi" :percentage="result.weightPercentage"/>
				<Medium v-if="result.score == 1 && !displayErrorMessage && !lowerBodyAmputations && !upperBodyAmputations" :bmi="result.bmi" :percentage="result.weightPercentage"/>
				<High v-if="result.score >= 2 && !displayErrorMessage && !lowerBodyAmputations && !upperBodyAmputations" :bmi="result.bmi" :percentage="result.weightPercentage"/>
				<Answers :answers="answers" :error="displayErrorMessage" :result="result"/>			
			</div>
			<!-- Calculator Controls -->
			<div class="control-holder">
				<button id="calculate" class="btn btn-primary mr-1 mr--1" @click="calculateclicked()" type="button" v-if="!displayresult"><span v-if="isHidden == false">Calculate</span><span v-if="isHidden == true"><i class="fa fa-circle-o-notch fa-spin"></i> Loading</span></button>
				<button id="reset" class="btn btn-primary mr-1 mr--1" type="button" @click="reset()" v-if="displayresult">Reset</button>
				<button id="copyButton" class="btn btn-primary mr-1 mr--1" type="button" @click="copyToClipboard()" v-if="displayresult">Copy Result</button>
			</div>
		</div>
		
		<Content />
		
	</div>
</template>

<script>
	import Content from '@/components/content/Content';
	import Warning from '@/components/content/Warning';
	import Tabs from '@/components/content/Tabs';
	import InputWeight from '@/components/inputs/InputWeight';
	import InputWeightPrevious from '@/components/inputs/InputWeightPrevious';
	import InputHeight from '@/components/inputs/InputHeight';
	import InputText from '@/components/inputs/InputText';
	import SelectParts from '@/components/inputs/SelectParts';
	import Question from '@/components/inputs/Question';
	import Answers from '@/components/content/Answers';
	import Responses from '@/components/content/Responses';
	import Low from '@/components/responses/low';
	import Medium from '@/components/responses/medium';
	import High from '@/components/responses/high';
	import dataQuestions from "@/components/data/questions.json";
	import dataAdditional from "@/components/data/additional.json";
	import dataInputs from "@/components/data/inputs.json";
	import "isomorphic-fetch";

	export default {
		name: "Calculator",
		components: {
			Low,
			Medium,
			High,
			Content,
			Warning,
			InputWeight,
			InputWeightPrevious,
			InputHeight,
			InputText,
			SelectParts,
			Question,
			Answers,
			Responses,
			Tabs
		},

		data: function(){
			return this.initialState();
		},
		mounted() {
			this.answers.currentWeight = this.$refs.currentWeight.$data;
			this.answers.previousWeight = this.$refs.monthsAgo.$data;
			this.answers.haveOedema = this.$refs.haveOedema.$data,				
			this.answers.isOedema = this.$refs.isOedema.$data,
			this.answers.oedemaPresent = this.$refs.present.$data,
			this.answers.isAmputations = this.$refs.isAmputations.$data;
			this.answers.amputations = this.$refs.amputations.$data;
			this.answers.weightLossPlanned = this.$refs.weightLoss.$data;
			this.answers.currentHeight = this.$refs.currentHeight.$data;
			this.answers.dateCurrentMeasured = this.$refs.dateCurrent.$data;
			this.answers.acuteDiseaseEffect = this.$refs.acute.$data;
		},
		methods: {
				
			handleClick(e) {
				if (e.target.matches("a")) {
					let url = e.target.href;
					let target = e.target.target == "_blank" ? "_blank" : "_self";
					let dataId = e.target.dataset.id == null ? -1 : e.target.dataset.id;
					var isnativeapp = document.getElementById("isnativeapp");
					if (isnativeapp === undefined || isnativeapp === null) 
					{
						window.open(url,target);  
					}
					else{
						if(target == "_blank") {
							let data = {title:'',nodeid: dataId, href:url, target: target};
							window.Native('dataCallback', data);
						} 
					}	
				}
			},
			
			initialState() {
				return {
					answers: {
						currentWeight: {
							kg: null,
							st: null,
							lb: null
						},
						previousWeight:  {
							kg: null,
							st: null,
							lb: null
						},
						haveOedema: {
							text: null,
							value: null
						},
						isOedema: {
							text: null,
							value: null
						},
						oedemaPresent: {
							text: null,
							value: null
						},
						weightLossPlanned: {
							text: null,
							value: null
						},
						isAmputations: {
							text: null,
							value: null
						},
						amputations: [],
						currentHeight: {
							cm: null,
							ft: null,
							inches: null
						},
						dateCurrentMeasured: null,
						acuteDiseaseEffect: {
							text: null,
							value: null
						},
					},
					inputQuestions: dataInputs,					
					questions: dataQuestions,
					additionalQuestions: dataAdditional,
					result: {
						bmi: null,
						bmiScore: null,
						adjustedCurrentWeight: {
							kg: null,
							st: null,
							lb: null
						},
						adjustedPreviousWeight: {
							kg: null,
							st: null,
							lb: null
						},
						isOedema: false,
						haveOedema: false,
						haveAmputations: false,
						weightPercentage: null,
						weightScore: null,
						adeScore: null,
						score: 0,
						text: "",
					},
					exceededWeight: false,
					exceededWeightAdjusted: false,
					upperBodyAmputations: false,
					lowerBodyAmputations: false,
					displayresult: false,
					displayErrorMessage: false,
					showform: false,
					isHidden: false,
					InvalidBmi: false,
					InvalidWeightLoss: false
				};
			},

			kgToStPo (objKg) {
				let kilograms = parseFloat(parseFloat(objKg.kg).toFixed(2));
				let stone = parseFloat(Math.floor(kilograms * 0.157473));
				let pounds = parseFloat(Math.round(kilograms * 2.2046) - (stone * 14));
				if(pounds >= 14){
					pounds = 0; stone++
				}
				objKg.st = stone;
				objKg.lb = pounds;
				return;
			},

			checkExceededWeight() {
				setTimeout(()=>{
					if(this.answers.previousWeight.kg == null || this.answers.previousWeight.kg == "" || this.answers.currentWeight.kg == null || this.answers.currentWeight.kg == "") {
						this.exceeded = false 
					} else {
						let exceeded = parseFloat(this.answers.previousWeight.kg - this.answers.currentWeight.kg);
						// console.log('weight difference:' + ' ' + exceeded);
						let checkWeight = exceeded > 15.4 ? true : false;
						this.exceededWeight = checkWeight;
					}
				}, 400);
				return;
			},
			
			checkWeight() {				
				let calcValue = 1;
				if(this.answers.isAmputations.text == "Yes") {			
						let getValue = this.answers.amputations.parts;	
						calcValue += getValue[0].answer.value;
						calcValue += getValue[1].answer.value;
						calcValue += getValue[2].answer.value;
						calcValue += getValue[3].answer.value;
						calcValue += getValue[4].answer.value;
						calcValue += getValue[5].answer.value;
				} 
				let haveOedema = this.answers.haveOedema.text == "Yes";
				let isOedema = haveOedema && this.answers.isOedema.value != null ? this.answers.isOedema.value : 0;
				let calcCurrentOedema = this.answers.currentWeight.kg + isOedema;
				let calcCurrentWeight = calcCurrentOedema * calcValue;
				let calcPreviousWeight = this.answers.previousWeight.kg * calcValue;
	
				return calcCurrentWeight == (null || "") ||
						calcPreviousWeight == (null || "") ||
						calcCurrentWeight >= calcPreviousWeight;
			},

			checkAdditionalQuestions() {
				if (this.answers.haveOedema.value != null && this.answers.isAmputations.value != null) {
					if (this.answers.haveOedema.text == "Yes") {
						if (this.answers.isOedema.value == null || this.answers.oedemaPresent.value == null) {
							this.InvalidBmi = true;
							this.InvalidWeightLoss = true;
						}
					} else if (this.answers.isAmputations.text == "Yes") {
						if (this.answers.amputations.parts == null) {
							this.InvalidBmi = true;
							this.InvalidWeightLoss = true;
						}
					} else {
						this.InvalidBmi = false;
						this.InvalidWeightLoss = false;
					}
				} else {
					this.InvalidBmi = true;
					this.InvalidWeightLoss = true;
				}
			},

			checkBmiValidity() {
				let currentWeight = this.answers.currentWeight.kg;
				let previousWeight = this.answers.previousWeight.kg;
				let currentHeight = this.answers.currentHeight.cm;

				if (currentWeight != null && currentHeight != null) {
					this.checkAdditionalQuestions();
				} else {
					this.InvalidBmi = true;
				}

				if (currentWeight != null && previousWeight != null) {
					this.checkAdditionalQuestions();
				} else {
					this.InvalidWeightLoss = true;
				}
			},
			
			checkAmpultations() {
				return this.answers.isAmputations.value != (null || 1);
			},
			
			checkIfNullOrEmpty(checkValue) {
				return checkValue > 0 || checkValue != null;
			},

			isNanWeight(input) { 
				input.kg = isNaN(input.kg) ? null : input.kg;
				input.st = isNaN(input.kg) ? null : input.st;
				input.lb = isNaN(input.kg) ? null : input.lb;
			},

			isNanHeight(input) { 
				input.cm = isNaN(input.cm) ? null : input.cm;
				input.ft = isNaN(input.cm) ? null : input.ft;
				input.inches = isNaN(input.cm) ? null : input.inches;
			},

			adeScoring() {
				if(this.answers.acuteDiseaseEffect.value == null) {	
					this.displayErrorMessage = true;
				} else {
					if(this.answers.acuteDiseaseEffect.text == "Yes") {
						this.result.adeScore = 2;
					} else {
						this.result.adeScore = 0;
					}				
				}
			},

			oedemaAdjustments() {
				if(this.answers.haveOedema.text != null) {
					if(this.answers.haveOedema.text == "Yes") {
						if(this.answers.isOedema.value != null || this.answers.oedemaPresent.value != null) {
							let currentWeight = this.answers.currentWeight.kg;
							let previousWeight = this.answers.previousWeight.kg;
							this.result.adjustedCurrentWeight.kg = currentWeight + this.answers.isOedema.value;
							if(this.answers.oedemaPresent.text == "Yes") {
								this.result.adjustedPreviousWeight.kg = previousWeight + this.answers.isOedema.value;
							} else {
								this.result.adjustedPreviousWeight.kg = previousWeight;
							}
						}
						else {
							this.displayErrorMessage = true;
						}
					} else {
						this.result.adjustedCurrentWeight.kg = this.answers.currentWeight.kg;
						this.result.adjustedPreviousWeight.kg = this.answers.previousWeight.kg;
					}
				} else {			
					this.displayErrorMessage = true;
				}
			},

			checkPlannedOrUnplanned() {
				let check = this.checkWeight();
				if(!check) {
					if(this.answers.weightLossPlanned.value == null) {
						console.log("weight loss is null");
						this.displayErrorMessage = true;
						this.InvalidWeightLoss = true;
					}
				}
			},

			calculatePercentage() {
				if(this.answers.currentWeight.kg != null && this.answers.previousWeight.kg != null) {
					let currentWeight = this.result.adjustedCurrentWeight.kg;
					let previousWeight = this.result.adjustedPreviousWeight.kg;
					let calcPercentage = 100 - ((currentWeight / previousWeight) * 100);
					let percentage = parseFloat(calcPercentage).toFixed(1);
					
					if(this.answers.weightLossPlanned.text == "Planned") {
						this.result.weightPercentage = "0";
					} else {
						this.result.weightPercentage = percentage;
					}
					
					if(this.result.weightPercentage < 5) {
						this.result.weightScore = 0;
					} else if (this.result.weightPercentage >= 5 && this.result.weightPercentage <= 10) {
						this.result.weightScore = 1;
					} else if (this.result.weightPercentage > 10) {
						this.result.weightScore = 2;
					}
					
					if(this.result.weightPercentage < 0) {						
						this.result.weightPercentage = 0;
					}

					if(this.answers.weightLossPlanned.text == "Yes") {
						this.result.weightPercentage = 0;
						this.result.weightScore = 0;
					} 


				} else {	
					this.displayErrorMessage = true;
				}
			},

			toggleAmputationMessage() {
				this.hideResult();
				let responses = this.answers.amputations.parts;
				console.log("toggle amp called");
				setTimeout(()=> {
					let topSection = responses[0].answer.text + responses[1].answer.text + responses[2].answer.text;
					let bottomSection = responses[3].answer.text + responses[4].answer.text + responses[5].answer.text;
					this.upperBodyAmputations = topSection > 2 ? true : false;
					this.lowerBodyAmputations = bottomSection > 2 ? true : false;
				},300);
			},

			calculateAmputations() {				
				if(this.answers.isAmputations.value == null) {	
					this.displayErrorMessage = true;
				} else {						
					if(this.answers.isAmputations.text == "Yes") {			
						let getValue = this.answers.amputations.parts;
						let topSection = getValue[0].answer.text + getValue[1].answer.text + getValue[2].answer.text;
						let bottomSection = getValue[3].answer.text + getValue[4].answer.text + getValue[5].answer.text;

						if (topSection + bottomSection == 0) {
							this.displayErrorMessage = true;
							this.InvalidBmi = true;
							this.InvalidWeightLoss = true;
						}
						else {
							let calcValue = 1;
							calcValue += getValue[0].answer.value;
							calcValue += getValue[1].answer.value;
							calcValue += getValue[2].answer.value;
							calcValue += getValue[3].answer.value;
							calcValue += getValue[4].answer.value;
							calcValue += getValue[5].answer.value;

							let calcCurrentWeight = this.result.adjustedCurrentWeight.kg.toFixed(2) * calcValue;			
							this.result.adjustedCurrentWeight.kg = calcCurrentWeight;

							if (this.result.adjustedPreviousWeight.kg != null) {
								let calcPreviousWeight = this.result.adjustedPreviousWeight.kg.toFixed(2) * calcValue;
								this.result.adjustedPreviousWeight.kg = calcPreviousWeight;
							}
								// this.toggleAmputationMessage();
						}
					}
					else if (this.answers.isAmputations.text == "No") {
						this.$refs.amputations.reset();
						this.upperBodyAmputations = false;
						this.lowerBodyAmputations = false;
					}
				}
			},

			calculateBmi() {				
				if(this.answers.currentWeight.kg != null && this.answers.currentHeight.cm != null) {
					let cmMtr;
					cmMtr = this.answers.currentHeight.cm / 100;
					let cmSq = cmMtr * cmMtr;
					if(this.result.adjustedCurrentWeight.kg != null) {
						this.result.bmi = parseFloat(this.result.adjustedCurrentWeight.kg / cmSq).toFixed(2);
					} else {
						this.result.bmi = parseFloat(this.answers.currentWeight.kg / cmSq).toFixed(2);
					} 
					if(this.result.bmi < 18.5) {
						this.result.bmiScore = 2;
					} else if (this.result.bmi >= 18.5 && this.result.bmi <= 20.0) {
						this.result.bmiScore = 1;
					} else if (this.result.bmi > 20) {
						this.result.bmiScore = 0;
					}
				}
				else {
					this.displayErrorMessage = true;
				}
				this.checkBmiValidity();
			},

			totalScore() {				
				this.result.score = this.result.weightScore + this.result.adeScore + this.result.bmiScore;
				if(this.result.score == 0) {
					this.result.text = "LOW";
				} else if(this.result.score == 1) {
					this.result.text = "MEDIUM";
				} else if(this.result.score >= 2) {
					this.result.text = "HIGH";
				}  
			},

			displayForm(){		
				this.showform = !this.showform;
			},

			hideResult(){	
				this.showform = true;		
				this.isHidden = false;
				this.InvalidBmi = false;
				this.InvalidWeightLoss = false;
				this.displayresult = false;
				this.displayErrorMessage = false;
				this.clearResult();
			},

			clearInputs() {
				document.getElementById("currentWeight-kg").value = "";
				document.getElementById("currentWeight-st").value = "";
				document.getElementById("currentWeight-st").value = "";
				document.getElementById("monthsAgo-kg").value = "";
				document.getElementById("monthsAgo-st").value = "";
				document.getElementById("monthsAgo-st").value = "";
				document.getElementById("currentHeight-cm").value = "";
				document.getElementById("currentHeight-ft").value = "";
				document.getElementById("currentHeight-inches").value = "";
				document.getElementById("dateMeasured").value = "";
				this.$refs.currentWeight.reset();
				this.$refs.currentHeight.reset();
				this.$refs.monthsAgo.reset();
				this.$refs.isAmputations.reset();
				this.$refs.amputations.reset();
				this.$refs.weightLoss.reset();				
				this.$refs.haveOedema.reset();
				this.$refs.isOedema.reset();
				this.$refs.present.reset();
				this.$refs.acute.reset();
				this.$refs.dateCurrent.reset();
			},

			reset() {
				this.clearInputs();
				this.clearResult();
				this.isHidden = false;
				this.InvalidBmi = false;
				this.InvalidWeightLoss = false;
				this.displayresult = false;
				this.displayErrorMessage = false;
				this.upperBodyAmputations = false;
				this.lowerBodyAmputations = false;
				this.exceededWeight = false;
				this.exceededWeightAdjusted = false;
				this.
				window.scrollTo(0, 0);
			},

			clearResult() {
				this.result.bmi = null;
				this.result.bmiScore = null;
				this.result.adjustedCurrentWeight.kg = null;
				this.result.adjustedCurrentWeight.st = null;
				this.result.adjustedCurrentWeight.lb = null;
				this.result.adjustedPreviousWeight.kg = null;
				this.result.adjustedPreviousWeight.st = null;
				this.result.adjustedPreviousWeight.lb = null				
				this.result.isOedema = false;
				this.result.haveOedema = false;
				this.result.haveAmputations = false;
				this.result.weightPercentage = null;
				this.result.weightScore = null;
				this.result.adeScore = null;
				this.result.score = null;
				this.result.text = "";
			},

			calculateclicked() {
				this.isHidden = true;
				setTimeout(() => {
					this.showform = false;
					this.displayresult = true;				
					this.isNanHeight(this.answers.currentHeight);
					this.isNanWeight(this.answers.currentWeight);
					this.isNanWeight(this.answers.previousWeight);
					this.isNanWeight(this.result.adjustedCurrentWeight);
					this.isNanWeight(this.result.adjustedPreviousWeight);
					this.result.adjustedCurrentWeight.kg = this.answers.currentWeight.kg;
					this.result.adjustedPreviousWeight.kg = this.answers.previousWeight.kg;		
					
					this.oedemaAdjustments();
					this.calculateAmputations();		
					this.calculatePercentage();

					let exceeded = parseFloat(this.result.adjustedPreviousWeight.kg - this.result.adjustedCurrentWeight.kg);
					let checkWeight = exceeded > 15.4 ? true : false;
					this.exceededWeightAdjusted = checkWeight;

					this.calculateBmi();				
					this.adeScoring();			
					this.checkPlannedOrUnplanned();		
					this.kgToStPo(this.result.adjustedCurrentWeight);
					this.kgToStPo(this.result.adjustedPreviousWeight);
					this.totalScore();
				}, 250);
		
			},

			toggleErrorMessage() {
				if (this.answered_count > 0) {
					this.displayError = true;
					setTimeout(()=> {
						document.getElementById("error").scrollIntoView(true)},100);
				} else {
					this.displayError = false;
				}
			},

			getDateToday() {
				var date = "";
				var today = new Date();
				date = today.getDate() + "/" + (today.getMonth() + 1) + "/" + today.getFullYear() + " " + (today.getHours() < 10 ? "0" + today.getHours() : today.getHours()) + ":" + (today.getMinutes() < 10 ? "0" + today.getMinutes() : today.getMinutes());
				return date;
			},

			anchorLink(i) {
				var element = document.getElementById(i);
				element.scrollIntoView();
			},

			copyToClipboard() {
				var range;
				var finalText = "";
				if (document.selection) { // IE
					range = document.body.createTextRange();
					range.moveToElementText(document.getElementById('calculator-copy'));
					range.select();
					finalText = this.getDateToday() + "\n\nMalnutrition Universal Screening Tool (MUST)\n\n" + range + "\n\nEnter action taken as a result of this alert";
					document.selection.empty();
				} else if (window.getSelection) {
					range = document.createRange();
					range.selectNode(document.getElementById('calculator-copy'));
					window.getSelection().removeAllRanges();
					window.getSelection().addRange(range);
					finalText = this.getDateToday() + "\n\nMalnutrition Universal Screening Tool (MUST)\n\n" + window.getSelection().toString() + "\n\nEnter action taken as a result of this alert";
					window.getSelection().removeAllRanges();
				}
				var dummy = document.createElement("textarea");
				// Add it to the document
				document.body.appendChild(dummy);
				// Set its ID
				dummy.setAttribute("id", "dummy_id");
				// Output the array into it
				document.getElementById("dummy_id").value = finalText;
				// Select it
				dummy.select();
				// Copy its contents
				document.execCommand("copy");
				// Remove it as its not needed anymore
				document.body.removeChild(dummy);
				var clicked = false;
				var copyButton = document.getElementById('copyButton');
				var last = copyButton.innerHTML;
				if (!clicked) {
					copyButton.innerHTML = 'Copied!';
					clicked = true;
					setTimeout(function () {
						copyButton.innerHTML = last;
						clicked = false;
					}.bind(copyButton), 3000);
				}
			}
		}
	};
</script>